import i18n from 'i18n-js';
import * as types from './types';
import { getErrorMessage, parsePagination } from 'utils';
import { uiActions } from 'state/modules/UI';
import * as usersActions from 'services/api/users';
import * as accountActions from 'services/api/account';
import * as creditActions from 'services/api/credit';
import * as userGroupsActions from 'services/api/userGroups';

// Get User Account
const fetchAccountByIdRequest = () => ({
  type: types.FETCH_ACCOUNT_BY_ID_REQUEST,
});

const fetchAccountByIdSuccess = (payload) => ({
  type: types.FETCH_ACCOUNT_BY_ID_SUCCESS,
  payload,
});

const fetchAccountByIdFailed = () => ({
  type: types.FETCH_ACCOUNT_BY_ID_FAILED,
});

export const fetchAccountById = (id) => {
  return async (dispatch) => {
    dispatch(fetchAccountByIdRequest());

    const response = await accountActions.fetchAccountById(id);

    if (response.ok) {
      return dispatch(fetchAccountByIdSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccountByIdFailed(error));
  };
};

// Update Account
const fetchUpdateAccountRequest = () => ({
  type: types.FETCH_UPDATE_CUSTOMER_ACCOUNT_REQUEST,
});

const fetchUpdateAccountSuccess = (payload) => ({
  type: types.FETCH_UPDATE_CUSTOMER_ACCOUNT_SUCCESS,
  payload,
});

const fetchUpdateAccountFailed = () => ({
  type: types.FETCH_UPDATE_CUSTOMER_ACCOUNT_FAILED,
});

export const fetchUpdateAccount = (id, payload) => {
  return async (dispatch) => {
    dispatch(fetchUpdateAccountRequest());

    const response = await accountActions.fetchUpdateAccount(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payments.account_updated'),
        })
      );
      return dispatch(fetchUpdateAccountSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchUpdateAccountFailed(error));
  };
};

export const resetUpdateAccount = () => (dispatch) => {
  return dispatch({
    type: types.RESET_UPDATE_ACCOUNT,
  });
};

// Get User Account Credit
const fetchAccountCreditByIdRequest = () => ({
  type: types.FETCH_ACCOUNT_CREDIT_BY_ID_REQUEST,
});

const fetchAccountCreditByIdSuccess = (payload) => ({
  type: types.FETCH_ACCOUNT_CREDIT_BY_ID_SUCCESS,
  payload,
});

const fetchAccountCreditByIdFailed = () => ({
  type: types.FETCH_ACCOUNT_CREDIT_BY_ID_FAILED,
});

export const fetchAccountCreditById = (params) => {
  return async (dispatch) => {
    dispatch(fetchAccountCreditByIdRequest());

    const response = await creditActions.fetchAccountCreditById(params);
    const pagination = parsePagination(response.headers);
    if (response.ok) {
      return dispatch(
        fetchAccountCreditByIdSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAccountCreditByIdFailed(error));
  };
};

// Add credit
const fetchAddCreditRequest = () => ({
  type: types.FETCH_ADD_CREDIT_REQUEST,
});

const fetchAddCreditSuccess = () => ({
  type: types.FETCH_ADD_CREDIT_SUCCESS,
});

const fetchAddCreditFailed = () => ({
  type: types.FETCH_ADD_CREDIT_FAILED,
});

export const fetchAddCredit = (payload) => {
  return async (dispatch) => {
    dispatch(fetchAddCreditRequest());

    const response = await creditActions.fetchAddCredit(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payments.credit_applied'),
        })
      );
      dispatch(fetchAccountCreditById({ account_id: payload.account_id }));
      return dispatch(fetchAddCreditSuccess());
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchAddCreditFailed(error));
  };
};

export const resetAddCredit = () => (dispatch) => {
  return dispatch({
    type: types.RESET_ADD_CREDIT,
  });
};

// Add nfc card
const fetchAddNfcCardRequest = () => ({
  type: types.FETCH_ADD_NFC_CARD_REQUEST,
});

const fetchAddNfcCardSuccess = () => ({
  type: types.FETCH_ADD_NFC_CARD_SUCCESS,
});

const fetchAddNfcCardFailed = () => ({
  type: types.FETCH_ADD_NFC_CARD_FAILED,
});

export const fetchAddNfcCard = (payload) => {
  return async (dispatch) => {
    dispatch(fetchAddNfcCardRequest());

    const response = await usersActions.fetchAddNfcCard(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('users.nfc_success_message'),
        })
      );
      return dispatch(fetchAddNfcCardSuccess());
    }

    const error = getErrorMessage(response.data);

    dispatch(uiActions.handleShowSnackbar({ type: 'error', message: error[0]?.message || error }));

    dispatch(fetchAddNfcCardFailed());
  };
};

export const resetAddNfcCard = () => (dispatch) => {
  return dispatch({
    type: types.RESET_ADD_NFC_CARD,
  });
};

// Revoke credit
const fetchRevokeCreditRequest = () => ({
  type: types.FETCH_REVOKE_CREDIT_REQUEST,
});

const fetchRevokeCreditSuccess = (payload) => ({
  type: types.FETCH_REVOKE_CREDIT_SUCCESS,
  payload,
});

const fetchRevokeCreditFailed = () => ({
  type: types.FETCH_REVOKE_CREDIT_FAILED,
});

export const fetchRevokeCredit = (id, account) => {
  return async (dispatch) => {
    dispatch(fetchRevokeCreditRequest());

    const response = await creditActions.fetchRevokeCredit(id);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payments.credit_revoked'),
        })
      );
      dispatch(fetchAccountCreditById({ account_id: account }));
      return dispatch(fetchRevokeCreditSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchRevokeCreditFailed(error));
  };
};

// Invite User
const fetchInviteUserRequest = () => ({
  type: types.FETCH_INVITE_USER_REQUEST,
});

const fetchInviteUserSuccess = (payload) => ({
  type: types.FETCH_INVITE_USER_SUCCESS,
  payload,
});

const fetchInviteUserFailed = (errors) => ({
  type: types.FETCH_INVITE_USER_FAILED,
  errors,
});

export const resetInviteFormErrors = () => (dispatch) =>
  dispatch({
    type: types.RESET_INVITE_FORM_ERRORS,
  });

export const fetchInviteUser = (id, payload, onSuccess) => {
  return async (dispatch) => {
    dispatch(fetchInviteUserRequest());

    const response = await userGroupsActions.fetchInviteUser(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('users.customer_invited_msg'),
        })
      );

      if (onSuccess) onSuccess();

      return dispatch(fetchInviteUserSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data, { fullObject: true });

    if (typeof formattedError === 'string') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
      return dispatch(fetchInviteUserFailed());
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(fetchInviteUserFailed(errors));
  };
};
